import moment from 'moment';

const m = d => moment(d, 'DD/MM/YYYY');

/**
 * Template for a person
 * @return {
 *      {name: string,
 *       dateOfBirth: string,
 *       onsetOfSymptoms: string,
 *       ageAtDeath: string,
 *       currentAge: number,
 *       gender: string,
 *       predictedDeath: string,
 *       income: object,
 *       lifeExpectancy: object,
 *    }
 * }
 */
export function personTemplate() {
    return {
        id: null,
        name: null,
        dateOfBirth: null,
        dateOfDeath: null,
        onsetOfSymptoms: null,
        ageAtDeath: null,
        currentAge: null,
        gender: null,
        predictedDeath: null,
        income: incomeTemplate(),
        lifeExpectancy: lifeExpectancyTemplate(),
        assumedLifeExpectancy: assumedLifeExpectancyTemplate(),
        levelOfEducation: null,
        employedAtDateOfDeath: null,
        disabledAtAccident: null,
        employedAtAccident: null,
        disabledAtTrial: null,
        employedAtTrial: null,
        employedButFor: null,
        ageAtOnset: null,
    };
}

/**
 * Template for a persons life expectancy
 * @return {
 *        {method: string,
 *         yearsAndMonths: {
 *            months: number,
 *            years: number
 *         },
 *         dateOfDeathIsAssumed: null|string,
 *         ageAtDeath: null|number,
 *         dateOfDeath: null|string,
 *         percentageOfNormalLifeExpectancy: null|number,
 *         percentageReductionInNormalLifeExpectancy: null|number
 *     }
 * }
 */
export function lifeExpectancyTemplate() {
    return {
        method: 'ogden_table',
        yearsAndMonths: {
            months: 0,
            years: 0,
        },
        dateOfDeathIsAssumed: null,
        ageAtDeath: null,
        dateOfDeath: null,
        percentageOfNormalLifeExpectancy: 100,
        percentageReductionInNormalLifeExpectancy: 0,
        child: {
            youthCutOffAge: null,
            youthYearsLeft: null,
            youthCutOffDate: null,
        },
    };
}

/**
 * Template for a persons life expectancy
 * @return {
 *        {method: string,
 *         yearsLeft: null|number
 *         ageAtDeath: null|number,
 *         dateOfDeath: null|string
 *     }
 * }
 */
export function assumedLifeExpectancyTemplate() {
    return {
        method: null,
        yearsLeft: 0,
        monthsLeft: 0,
        ageAtDeath: '',
        dateOfDeath: '',
    };
}

/**
 * Template for storing a persons income
 * @return {
 *      {retired: boolean,
 *       retirementAge: number,
 *       retirementDate: string,
 *       periodResults: Array
 *     }
 * }
 */
export function incomeTemplate() {
    return {
        pastLossOfEarnings: null,
        futureLossOfEarnings: null,
        absences: null,
        retired: false,
        retirementAgeCalc: 'auto',
        retirementAge: 65,
        retirementDate: '',
        periods: [],
        futurePeriods: [],
        pastPeriods: [],
        preAccident: earningsAtInjuryTemplate(),
        postInjuryPeriods: [],
        futureEarningsPreAccident: earningsAtInjuryTemplate(),
        futureEarningsPostAccident: earningsAtInjuryTemplate(),
        absencePeriods: [absenceTemplate()],
        otherPayments: 0,
        contingencyMultiplier: false,
        multiplier: 1,
        ownPastLosses: false,
        ownFutureLosses: false,
        pastLossOfEarningsNetTotal: 0,
    };
}

/**
 * Template for storing a persons income period
 * @return {
 *      {startOfPeriod: date,
 *       endOfPeriod: date,
 *       employmentType: string,
 *       employmentIncome: number,
 *       employmentNetGross: string,
 *       pensionIncome: number,
 *       pensionNetGross: string,
 *       totalGrossIncome: number,
 *       totalNetIncome: number,
 *     }
 * }
 */
export const defaultIncomePeriodTemplate = ({start, end, employmentNetGross = 'net', employmentType = 'employed', employmentIncome = 0, totalGross = 0, totalNetIncome = 0}) => {
    return {
        startOfPeriod: start,
        endOfPeriod: end,
        employmentType: employmentType,
        employmentIncome: employmentIncome,
        employmentNetGross: employmentNetGross,
        pensionIncome: 0,
        pensionNetGross: 'net',
        totalGrossIncome: totalGross,
        totalNetIncome: totalNetIncome,
        duration: Math.round(m(end).diff(m(start), 'years', true) * 100) / 100
    };
};

/**
 * Template for storing a custom heads of loss
 * @return {
 *      {name: string,
 *       value: number,
 *     }
 * }
 */
export function customHeadsOfLossTemplate() {
    return {
        name: '',
        value: 0,
        interest: 0.5,
        interestAccrued: 0,
    };
}

/**
 * Template for storing care
 * @return {
 *      {type: string,
 *       value: number,
 *       periods: array,
 *     }
 * }
 */
export function careTemplate() {
    return {
        type: null,
        past: {
            type: 'own',
            totalValue: 0,
            periods: [carePeriodTemplate()],
        },
        future: {
            type: 'own',
            totalValue: 0,
            periods: [carePeriodTemplate()],
        }
    };
}

/**
 * Template for storing a care periods
 * @return {
 *      {periodStart: date,
 *       periodEnd: date,
 *       hoursPerDay: number,
 *       numberOfDays: number,
 *       rate: string,
 *       total: number,
 *       gratuitousCare: boolean
 *     }
 * }
 */
export function carePeriodTemplate() {
    return {
        periodStart: '',
        periodEnd: '',
        hoursPerDay: 0,
        numberOfDays: 0,
        rate: 'basic',
        total: 0,
        gratuitousCare: false,
        includeLastDay: true,
        includeFirstDay: true
    };
}

/**
 * Template for storing a loss of earnings periods
 * @return {
 *      {periodStart: date,
 *       periodEnd: date,
 *       rate: string,
 *       total: number,
 *       includeFirstDay: boolean,
 *       includeLastDay: boolean,
 *     }
 * }
 */
export function lossOfEarningsPeriodTemplate() {
    return {
        periodStart: '',
        periodEnd: '',
        periodTotal: 0,
        includeLastDay: true,
        includeFirstDay: true,
        periodNetGross: 'gross',
        periodEmploymentType: 'employed',
        periodValue: 0,
        duration: 0
    };
}

/**
 * Payment Template
 *
 * @return {
 *      {date: date,
 *       value: number,
 *      }
 * }
 */
export function paymentsTemplate() {
    return {
        date: '',
        value: 0,
    };
}

/**
 * Service Template
 *
 * @return {
 *      {descriptionOfService: null,
 *      calculatedDependency: null,
 *      ogdenTableF: null,
 *      ogdenTableE: null,
 *      ogdenTableTwentyEight: null,
 *      selectedOgdenDiscount: null,
 *      duration: 0,
 *      multiplicand: 0,
 *      toDate: '',
 *      durationType: 'life',
 *      valid: false,
 *      }
 * }
 */
export function serviceTemplate() {
    return {
        descriptionOfService: null,
        calculatedDependency: null,
        ogdenTableF: null,
        ogdenTableE: null,
        ogdenTableTwentyEight: null,
        selectedOgdenDiscount: 28,
        duration: 0,
        multiplicand: 0,
        toDate: '',
        durationType: 'life',
        valid: false,
        years: 0,
        months: 0,
        age: 0
    };
}

export const headOfLossTemplate = () => ({
    name: '',
    value: 0,
    interest: 0.5,
    interestAccrued: 0,
});

export const dependantsHeadsOfLossTemplate = () => ({
    generalDamages: 0,
    bereavementAward: 0,
    funeralExpenses: 0,
    lossOfSpouse: 0,
    pastLossOfEarnings: 0,
    pastCostOfCare: 0,
    pastCostOfTravel: 0,
    misc: 0,
    interest: 0,
    dependantsCustomHeadsOfLoss: [],
});

export const dependantsCustomHeadsOfLossTemplate = () => ({
    name: '',
    value: 0,
});

export function earningsAtInjuryTemplate() {
    return {
        employmentType: 'employed',
        employmentIncome: 0,
        employmentNetGross: 'gross',
        totalNetIncome: 0,
    };
}

export function postInjuryIncomeTemplate({start, end}) {
    return {
        startOfPeriod: start,
        endOfPeriod: end,
        employmentType: '',
        employmentIncome: 0,
        employmentNetGross: 'gross',
        totalNetIncome: 0,
        totalNetLoss: 0,
        paymentsReceived: []
    };
};

export function paymentsReceivedTemplate() {
    return {
        date: '',
        total: 0
    };
}

export function absenceTemplate() {
    return {
        startOfPeriod: '',
        endOfPeriod: '',
        duration: 0
    };
}

export function purchaseTemplate() {
    return {
        description: null,
        purchaseDate: null,
        purchaseUntil: 'own',
        purchaseEnd: null,
        resaleOption: false,
        type: null,
        mortalityMultiplier: null,
        frequency: 0,
        frequencyType: 'years',
        resaleValue: 0,
        resaleTotal: 0,
        ageAtPurchase: null,
        multiplier: 0,
        multiplicand: 0,
        discount: 0,
        total: 0
    };
}

export function otherFutureLossesCareTemplate() {
    return {
        description: null,
        careStartDate: null,
        careEndDate: null,
        duration: 0,
        multiplier: 0,
        multiplicand: 0,
        lossesForLife: null,
        futureCareTotal: 0,
        mortalityMultiplier: null,
        ageAtEndDate: 0
    };
}

export function otherFutureLossesServicesTemplate() {
    return {
        description: null,
        servicesStartDate: null,
        servicesEndDate: null,
        duration: 0,
        multiplier: 0,
        multiplicand: 0,
        lossesForLife: null,
        futureServicesTotal: 0,
        mortalityMultiplier: null,
        ageAtEndDate: 0
    };
}

export function generalDamagesTemplate() {
    return {
        generalDamages: null,
        uplift: true,
        path: [],
        discount: 0,
        total: null
    };
}


// WEBPACK FOOTER //
// ./src/store/modules/shared/templates.js